/**
 * Remembers when each pending invite first reached us, so the Y / N hotkeys act on the newest live
 * one and the toast fires once per invite. The server's expiry clock isn't ours, so "live" counts
 * from arrival (see chooseInvite). Pure: fed with store snapshots, no DOM.
 */
import type { GangInviteView } from '../../shared/messages/gangs';
import { chooseInvite, type InviteChoice } from './socialFormat';

type CrewInvite = { crew: number; from: string; expiresAt: number };

export interface InviteArrivals {
  gang: GangInviteView[];
  crew: CrewInvite | null;
}

export class InviteTracker {
  private readonly gangSeen = new Map<number, number>();
  private readonly gangDismissed = new Set<number>();
  private crewRef: CrewInvite | null = null;
  private crewSeenAt = 0;
  private crewDismissed = false;

  /** Fold in the store's current invites; returns the ones we have not seen before (for the toast). */
  sync(invites: readonly GangInviteView[], crew: CrewInvite | null, now: number): InviteArrivals {
    const fresh: GangInviteView[] = [];
    const ids = new Set(invites.map((i) => i.id));
    for (const i of invites) {
      if (!this.gangSeen.has(i.id)) {
        this.gangSeen.set(i.id, now);
        fresh.push(i);
      }
    }
    for (const id of [...this.gangSeen.keys()]) {
      if (!ids.has(id)) {
        this.gangSeen.delete(id);
        this.gangDismissed.delete(id);
      }
    }
    let freshCrew: CrewInvite | null = null;
    if (crew !== this.crewRef) {
      this.crewRef = crew;
      this.crewDismissed = false;
      if (crew) {
        this.crewSeenAt = now;
        freshCrew = crew;
      }
    }
    return { gang: fresh, crew: freshCrew };
  }

  /** The invite Y / N act on, or null. */
  choose(now: number): InviteChoice | null {
    const gang = [...this.gangSeen].filter(([id]) => !this.gangDismissed.has(id)).map(([id, seenAt]) => ({ id, seenAt }));
    const crew = this.crewRef && !this.crewDismissed ? { id: this.crewRef.crew, seenAt: this.crewSeenAt } : null;
    return chooseInvite(gang, crew, now);
  }

  /** The crew invite to show in the crew panel (same liveness rule as the hotkeys), or null. */
  liveCrew(now: number): CrewInvite | null {
    if (!this.crewRef || this.crewDismissed) return null;
    return chooseInvite([], { id: this.crewRef.crew, seenAt: this.crewSeenAt }, now) ? this.crewRef : null;
  }

  dismissGang(id: number): void {
    this.gangDismissed.add(id);
  }

  dismissCrew(): void {
    this.crewDismissed = true;
  }

  reset(): void {
    this.gangSeen.clear();
    this.gangDismissed.clear();
    this.crewRef = null;
    this.crewDismissed = false;
  }
}
